import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Table, InputNumber, Input, Button, message, Space, Tag, Divider
} from 'antd';
import { SaveOutlined, AimOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { getDataForms, PHD_TYPES, isValidYear } from '../utils/dataForms';

const YEAR_MIN = 1900;
const YEAR_MAX = 2100;
const DEFAULT_RANGE = 5;
const STORAGE_KEY = 'phd_target_matrix_year_range';

/** 从 localStorage 恢复年份范围，失败则返回默认值 */
function loadSavedYearRange() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const now = new Date().getFullYear();
      if (parsed && typeof parsed.start === 'number' && typeof parsed.end === 'number'
          && parsed.start >= YEAR_MIN && parsed.end <= YEAR_MAX && parsed.start <= parsed.end) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return { start: new Date().getFullYear(), end: new Date().getFullYear() + DEFAULT_RANGE };
}

/**
 * 博士授权点目标管理矩阵组件
 *
 * 行 = 各指标下的任务项（field_keys），列 = 各年份 + 总目标
 * 支持年份范围设置（start_year / end_year）和逐列目标填写
 */
const PhdTargetMatrix = ({ department, targetRecords, onRefresh }) => {
  const [yearRange, setYearRange] = useState(loadSavedYearRange);
  const [matrixData, setMatrixData] = useState({});
  const [modifiedKeys, setModifiedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [startYearInput, setStartYearInput] = useState(String(yearRange.start));
  const [endYearInput, setEndYearInput] = useState(String(yearRange.end));

  // 记住年份范围选择
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(yearRange));
    } catch { /* ignore */ }
  }, [yearRange]);

  const dataForms = useMemo(() => getDataForms(department?.name || ''), [department?.name]);
  const phdForms = useMemo(
    () => dataForms.filter(f => PHD_TYPES.includes(f.key)),
    [dataForms]
  );

  // 从现有 targetRecords 构建初始矩阵数据
  useEffect(() => {
    if (!targetRecords) return;
    const data = {};
    const years = [];
    for (let y = yearRange.start; y <= yearRange.end; y++) years.push(y);

    phdForms.forEach(form => {
      const fields = form.fields.filter(f => f.key !== '年份' && f.key !== 'year');
      fields.forEach(field => {
        const key = `${form.key}::${field.key}`;
        data[key] = { total: null };
        years.forEach(y => { data[key][y] = null; });
      });
    });

    targetRecords.forEach(t => {
      if (!t.data_type || !t.field_key) return;
      const key = `${t.data_type}::${t.field_key}`;
      if (!data[key]) {
        data[key] = { total: null };
        years.forEach(y => { data[key][y] = null; });
      }
      // 优先年份范围，其次总目标（无年份），最后单一年份（旧格式兼容）
      if (t.start_year && t.end_year) {
        const sy = Math.max(t.start_year, yearRange.start);
        const ey = Math.min(t.end_year, yearRange.end);
        for (let y = sy; y <= ey; y++) {
          data[key][y] = t.target_value;
        }
      } else if (!t.start_year) {
        data[key].total = t.target_value;
      } else if (t.target_year >= yearRange.start && t.target_year <= yearRange.end) {
        data[key][t.target_year] = t.target_value;
      }
    });

    setMatrixData(data);
    setModifiedKeys(new Set());
  }, [targetRecords, phdForms, yearRange.start, yearRange.end]);

  const updateCell = (key, year, value) => {
    setMatrixData(prev => ({
      ...prev,
      [key]: { ...prev[key], [year]: value },
    }));
    setModifiedKeys(prev => new Set(prev).add(key));
  };

  // 年份输入通用处理
  const useYearInput = (value, setInput, onCommit) => {
    const handleChange = (val) => {
      const v = val.replace(/\D/g, '').slice(0, 4);
      setInput(v);
    };
    const handleBlur = () => {
      const y = Number(value);
      if (!isValidYear(y)) {
        onCommit((prev) => {
          setInput(String(prev));
          return prev;
        });
        message.warning(`请输入有效年份（${YEAR_MIN}-${YEAR_MAX}）`);
      } else {
        onCommit(y);
      }
    };
    return { handleChange, handleBlur };
  };

  const startYearCtrl = useYearInput(startYearInput, setStartYearInput, (valOrFn) => {
    if (typeof valOrFn === 'function') return; // blur fallback handled inline
    setYearRange(prev => ({ ...prev, start: valOrFn }));
  });
  const endYearCtrl = useYearInput(endYearInput, setEndYearInput, (valOrFn) => {
    if (typeof valOrFn === 'function') return;
    setYearRange(prev => ({ ...prev, end: valOrFn }));
  });

  // 优化：处理 blur 时的回退
  const handleYearStartBlur = () => {
    const y = Number(startYearInput);
    if (!isValidYear(y)) {
      setStartYearInput(String(yearRange.start));
      message.warning(`请输入有效年份（${YEAR_MIN}-${YEAR_MAX}）`);
    } else {
      setYearRange(prev => ({ ...prev, start: y }));
    }
  };

  const handleYearEndBlur = () => {
    const y = Number(endYearInput);
    if (!isValidYear(y)) {
      setEndYearInput(String(yearRange.end));
      message.warning(`请输入有效年份（${YEAR_MIN}-${YEAR_MAX}）`);
    } else {
      setYearRange(prev => ({ ...prev, end: y }));
    }
  };

  // 批量保存
  const handleSave = async () => {
    const targets = [];

    Object.entries(matrixData).forEach(([key, values]) => {
      const [dataType, fieldKey] = key.split('::');
      const form = phdForms.find(f => f.key === dataType);
      const field = form?.fields?.find(f => f.key === fieldKey);
      const fieldLabel = field?.label || fieldKey;

      if (values.total != null) {
        targets.push({
          department_id: department.id,
          data_type: dataType,
          field_key: fieldKey,
          field_label: fieldLabel,
          target_value: values.total,
        });
      }

      for (let y = yearRange.start; y <= yearRange.end; y++) {
        if (values[y] != null) {
          targets.push({
            department_id: department.id,
            data_type: dataType,
            field_key: fieldKey,
            field_label: fieldLabel,
            target_value: values[y],
            start_year: y,
            end_year: y,
            target_year: y,
          });
        }
      }
    });

    if (targets.length === 0) {
      message.warning('没有可保存的数据');
      return;
    }

    setSaving(true);
    try {
      await api.put('/indicators/targets/batch', { targets });
      message.success(`成功保存 ${targets.length} 项目标`);
      setModifiedKeys(new Set());
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error(error.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 构建列
  const columns = useMemo(() => {
    const cols = [
      {
        title: '指标类型',
        key: 'indicator',
        width: 100,
        fixed: 'left',
        render: (_, record) => {
          if (record.indicatorRowSpan === 0) {
            return { children: null, props: { rowSpan: 0 } };
          }
          return {
            children: <span style={{ fontWeight: 600, fontSize: 13 }}>{record.indicatorLabel}</span>,
            props: { rowSpan: record.indicatorRowSpan || 1 },
          };
        },
      },
      {
        title: '任务项',
        dataIndex: 'fieldLabel',
        key: 'fieldLabel',
        width: 260,
        fixed: 'left',
        render: (text) => (
          <span style={{
            paddingLeft: text.startsWith('其中:') ? 20 : 0,
            fontSize: 13,
          }}>
            {text}
          </span>
        ),
      },
      {
        title: '总目标',
        key: 'total',
        width: 100,
        align: 'center',
        render: (_, record) => (
          <InputNumber
            style={{ width: '90%' }}
            value={matrixData[record.key]?.total ?? null}
            onChange={val => updateCell(record.key, 'total', val)}
            min={0}
            precision={2}
            placeholder="总目标"
            size="small"
          />
        ),
      },
    ];

    for (let y = yearRange.start; y <= yearRange.end; y++) {
      cols.push({
        title: String(y),
        key: `y-${y}`,
        width: 90,
        align: 'center',
        render: (_, record) => (
          <InputNumber
            style={{ width: '85%' }}
            value={matrixData[record.key]?.[y] ?? null}
            onChange={val => updateCell(record.key, y, val)}
            min={0}
            precision={2}
            placeholder="—"
            size="small"
          />
        ),
      });
    }

    return cols;
  }, [matrixData, yearRange.start, yearRange.end]);

  // 构建行数据
  const rows = useMemo(() => {
    const result = [];
    phdForms.forEach(form => {
      const fields = form.fields.filter(f => f.key !== '年份' && f.key !== 'year');
      fields.forEach(field => {
        result.push({
          key: `${form.key}::${field.key}`,
          dataType: form.key,
          indicatorLabel: form.label,
          indicatorRowSpan: 0,
          fieldKey: field.key,
          fieldLabel: field.label,
        });
      });
    });

    // rowSpan 合并同指标行
    for (let i = 0; i < result.length;) {
      const label = result[i].indicatorLabel;
      let j = i + 1;
      while (j < result.length && result[j].indicatorLabel === label) j++;
      result[i].indicatorRowSpan = j - i;
      for (let k = i + 1; k < j; k++) result[k].indicatorRowSpan = 0;
      i = j;
    }

    return result;
  }, [phdForms]);

  if (!phdForms.length) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          该部门暂无博士授权点目标配置
        </div>
      </Card>
    );
  }

  const totalFields = rows.length;
  const hasModified = modifiedKeys.size > 0;

  return (
    <div>
      <Card
        size="small"
        title={
          <Space>
            <AimOutlined />
            <span>博士授权点目标矩阵</span>
            <Tag color="blue">{totalFields} 个数据项</Tag>
          </Space>
        }
        extra={
          <Space>
            <span style={{ color: '#666', fontSize: 13 }}>年份范围：</span>
            <Input
              value={startYearInput}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setStartYearInput(v);
              }}
              onBlur={handleYearStartBlur}
              onPressEnter={handleYearStartBlur}
              style={{ width: 80 }}
              size="small"
              maxLength={4}
              placeholder="起始年"
            />
            <span style={{ color: '#666' }}>—</span>
            <Input
              value={endYearInput}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setEndYearInput(v);
              }}
              onBlur={handleYearEndBlur}
              onPressEnter={handleYearEndBlur}
              style={{ width: 80 }}
              size="small"
              maxLength={4}
              placeholder="结束年"
            />
            <Divider type="vertical" />
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasModified}
            >
              保存全部
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <div style={{ marginBottom: 12, color: '#888', fontSize: 13 }}>
          填写各数据项在各年份的目标值。<strong>总目标</strong>为该数据项的累计目标（覆盖整个年份范围），各年份列可单独设定年度目标。
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="key"
          pagination={false}
          size="small"
          bordered
          scroll={{ x: Math.max(800, 360 + (yearRange.end - yearRange.start + 1) * 90) }}
        />
      </Card>
    </div>
  );
};

export default PhdTargetMatrix;
